import { TableHTMLAttributes, TdHTMLAttributes, HTMLAttributes, ReactNode, KeyboardEvent, ThHTMLAttributes, MouseEvent } from 'react';
import styles from './styles/Table.module.scss';
import cn from 'classnames';
import { useApplicationContainer } from 'containers/ApplicationContainer';

/* Table cells */

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {}

export function TableCell({ className, ...props }: TableCellProps) {
  return <td className={cn(styles.Cell, className)} {...props} />;
}

/* Table header cells */

interface TableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {}

export function TableHeaderCell({ className, align, ...props }: TableHeaderCellProps) {
  return <th className={cn(styles.HeaderCell, className)} align={align || 'left'} {...props} />;
}

interface TableHeaderToolTipCellProps extends TableHeaderCellProps {
  toolTip: ReactNode;
}

export function TableHeaderToolTipCell({ className, toolTip, children, align, ...props }: TableHeaderToolTipCellProps) {
  return (
    <TableHeaderCell className={cn(styles.ToolTipHeaderCell, className)} align={align || 'right'} {...props}>
      {children}
      <div className={styles.ToolTip}>{toolTip}</div>
    </TableHeaderCell>
  );
}

/* Table rows */

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  highlighted?: boolean;
}

export function TableRow({ className, highlighted, ...props }: TableRowProps) {
  return <tr className={cn(styles.Row, { [styles.Highlighted]: highlighted }, className)} {...props} />;
}

interface TableLinkRowProps extends TableRowProps {
  href?: string;
}

export function TableLinkRow({ className, href, onClick, children, ...props }: TableLinkRowProps) {
  const [, { handleOpenLink }] = useApplicationContainer();

  if (!href)
    return (
      <TableRow className={className} onClick={onClick} {...props}>
        {children}
      </TableRow>
    );

  function handleClick(e: MouseEvent<HTMLTableRowElement>) {
    handleOpenLink(href!);
    if (onClick) onClick(e);
  }

  return (
    <TableRow className={cn(styles.LinkRow, className)} onClick={handleClick} role="link" {...props}>
      {children}
    </TableRow>
  );
}

/* Table */

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  bottomBorder?: boolean;
  header?: ReactNode;
  topBorder?: boolean;
}

export default function Table({ children, className, bottomBorder, header, topBorder = true, ...props }: TableProps) {
  return (
    <table className={cn(styles.Table, { [styles.BottomBorder]: bottomBorder, [styles.TopBorder]: topBorder }, className)} {...props}>
      {header && (
        <thead>
          <tr className={styles.HeaderRow}>{header}</tr>
        </thead>
      )}
      <tbody>{children}</tbody>
    </table>
  );
}
